/**
 * Выбор флагов иностранного флота.
 *
 * Кнопка «Ин.флот» показывает всё сразу — от китайских сейнеров до норвежских
 * транспортов, а в рабочем районе их тысячи, и картина перестаёт читаться
 * (фидбек владельца 2026-08-07). Здесь — чистая часть выбора: разбор/запись в
 * ссылку, переключение и раскладка списка.
 *
 * Список флагов НЕ константа: он приходит с бэкендом по текущему кадру
 * (`meta.flags` со счётчиками). Зашитый список врал бы — набор флагов в
 * Беринговом море и в Японском разный.
 */
import { t } from '../i18n'

/** Сколько флагов показываем сразу; остальные — под «ещё N». */
export const FLAG_VISIBLE_TOP = 8

export interface FlagCount {
  code: string
  count: number
}

/** ISO alpha-3 → alpha-2: из alpha-2 складывается эмодзи флага (пара
 *  региональных индикаторов). Держим ровно те страны, что реально встречаются в
 *  районе (замер по кадру 40–60° с.ш., 140–179° в.д.: JPN 149, CHN 85, TWN 27,
 *  KOR 5, USA 2, NOR 1, CMR 1) плюс флаги удобства, — выдуманное соответствие
 *  нарисовало бы чужой флаг.
 *
 *  Этот же список задаёт, какие коды кабинет умеет НАЗВАТЬ: имена стран лежат в
 *  каталоге перевода как `flags.<ALPHA3>`. */
const ALPHA2: Record<string, string> = {
  RUS: 'RU', CHN: 'CN', KOR: 'KR', PRK: 'KP', JPN: 'JP', TWN: 'TW',
  NOR: 'NO', USA: 'US', CAN: 'CA', VUT: 'VU', PAN: 'PA', BLZ: 'BZ',
  LBR: 'LR', SLE: 'SL', CMR: 'CM', KHM: 'KH', TGO: 'TG', TZA: 'TZ',
  GEO: 'GE', MNG: 'MN', CYP: 'CY', MLT: 'MT', UKR: 'UA', POL: 'PL',
  LTU: 'LT', PRT: 'PT', ESP: 'ES',
}

/** Ключ подписи флага. Незнакомый код возвращается как есть: выдумывать
 *  название нельзя, а прятать судно из-за отсутствия перевода — тем более. */
export function flagLabelKey(code: string): string {
  return code in ALPHA2 ? `flags.${code}` : code
}

/** Название страны флага на языке кабинета. */
export function flagLabel(code: string): string {
  const key = flagLabelKey(code)
  return key === code ? code : t(key)
}

/** Эмодзи флага страны; null — если код незнаком (тогда показываем сам код,
 *  а не похожий флаг). */
export function flagEmoji(code: string): string | null {
  const a2 = ALPHA2[code]
  if (!a2) return null
  return String.fromCodePoint(...[...a2].map(c => 0x1f1e6 + c.charCodeAt(0) - 65))
}

/** Включён ли флаг СЕЙЧАС. Пустой выбор — это «весь инофлот», и чипы должны
 *  выглядеть нажатыми: иначе кнопка «Инофлот» показывает всех, а список внизу
 *  уверяет, что не выбрано ничего. */
export function isFlagOn(selected: string[], code: string): boolean {
  return selected.length === 0 || selected.includes(code)
}

/** Канонический порядок — по алфавиту: две одинаковые по смыслу ссылки должны
 *  выглядеть одинаково независимо от порядка нажатий. */
function canonical(flags: string[]): string[] {
  return [...new Set(flags)].sort()
}

/** Выбор → параметр URL. null — параметра нет (выбор пуст = все флаги). */
export function serializeFlags(flags: string[]): string | null {
  const c = canonical(flags)
  return c.length ? c.join(',') : null
}

/** Параметр URL → выбор. Мусор («CHN,, ,KOR») не должен ронять слой. */
export function parseFlags(raw: string | null): string[] {
  if (!raw) return []
  return canonical(raw.split(',').map(s => s.trim()).filter(Boolean))
}

/** Нажатие на чип.
 *
 *  Стартовое состояние — «выбраны все» (пустой фильтр), поэтому первое нажатие
 *  СНИМАЕТ страну со всего списка, а не выбирает её одну: человек, нажимающий на
 *  горящий чип, ждёт именно этого.
 *
 *  Пустой набор и полный — одно состояние: «весь инофлот», и в ссылку оно пишется
 *  пустым. Фильтр, оставляющий ноль судов, — это просто выключенная кнопка
 *  «Инофлот», отдельного способа получить пустую карту не нужно.
 */
export function toggleFlag(current: string[], code: string, all: string[] = []): string[] {
  const on = current.length ? current : [...all]
  const next = on.includes(code) ? on.filter(c => c !== code) : [...on, code]
  if (!next.length || (all.length > 0 && next.length >= all.length)) return []
  return canonical(next)
}

/** Раскладка списка: наверх — самые многочисленные, ПЛЮС всё СНЯТОЕ.
 *
 *  Наверху держим именно снятые флаги: по умолчанию горят все, и уехавшее под
 *  «ещё N» исключение выглядит как необъяснимо поредевшая карта. */
export function splitFlagList(
  counts: FlagCount[],
  selected: string[],
): { top: FlagCount[]; rest: FlagCount[] } {
  const top: FlagCount[] = []
  const rest: FlagCount[] = []
  for (const f of counts) {
    if (!isFlagOn(selected, f.code) || top.length < FLAG_VISIBLE_TOP) top.push(f)
    else rest.push(f)
  }
  return { top, rest }
}
